import { useI18N } from "@sundae/react-hooks";
import { Balance, Percentage, TableV2Types, tableV2Utils, Text } from "@sundae/ui-toolkit";
import { SundaeUtils } from "@sundaeswap/core/utilities";
import orderBy from "lodash/orderBy";
import { useCallback, useMemo, useState } from "react";
import AssetWithAssetName from "../../components/AssetWithAssetName";
import PoolComponent from "../../components/PoolComponent";
import { ADA_METADATA } from "../../constants/cardano.constants";
import { useProfileAppContext } from "../../stores";
import { IEnhancedMergedPosition, IEnhancedValue } from "../../types/Farm.types";
import { getAssetName } from "../../utils/assets.utils";
import useIsLPAsset from "../useIsLPAsset";

export type TPositionTableColumn = TableV2Types.ITableColumn<IEnhancedValue>[];

interface IUseYieldFarmingPositionTableArgs {
  mergedPosition?: IEnhancedMergedPosition;
}

export const useYieldFarmingPositionTable = ({
  mergedPosition,
}: IUseYieldFarmingPositionTableArgs) => {
  const { t, currentLocale } = useI18N("farms");
  const [sortConfig, setSortConfig] = useState<
    TableV2Types.ITableSortConfig<keyof IEnhancedValue | null>
  >({
    sortBy: null,
    direction: null,
  });
  const {
    state: { privacyEnabled },
  } = useProfileAppContext();
  const { isLPAsset } = useIsLPAsset();

  /**
   * This is the width of each column in the table.
   */
  const columnWidths = "2fr 1fr 1fr";

  /**
   * Handles Sorting of the table data.
   */
  const handleSort = useCallback(
    (sortBy: keyof IEnhancedValue) => {
      tableV2Utils.handleTableV2Sorting<IEnhancedValue>(sortConfig, sortBy, setSortConfig);
    },
    [sortConfig]
  );

  /**
   * This data that will be used to render the proper table headers.
   */
  const tableHeaders: TableV2Types.THeaders[] = useMemo(() => {
    return [
      { sortingKey: "asset", label: t("portfolio.positions.tableHeader.token") },
      { sortingKey: "amount", label: t("portfolio.positions.tableHeader.amount") },
      { sortingKey: "percentageLocked", label: "%" },
    ];
  }, [t]);

  /**
   * This is the data that will be used to render the proper columns in the table.
   */
  const tableData = useMemo(() => {
    const result =
      (mergedPosition?.value?.map((data) => ({
        data,
      })) as TableV2Types.TTableRowData<IEnhancedValue>[]) ??
      ([{ data: {}, tableRowProps: {} }] as TableV2Types.TTableRowData<IEnhancedValue>[]);

    // Sort the data based on the sortConfig
    if (sortConfig.sortBy) {
      const { sortBy, direction } = sortConfig;

      return orderBy(
        result,
        [
          ({ data: { asset, amount, percentageLocked } }) => {
            switch (sortBy) {
              case "amount":
                return amount;
              case "asset":
                return asset.metadata.assetId;
              default:
                return percentageLocked;
            }
          },
        ],
        [direction as boolean | "desc" | "desc"]
      );
    }

    // Fall back to default sorting (ADA first, then by price, then by total value)
    return orderBy(
      result,
      [
        ({ data: { asset } }) => SundaeUtils.isAdaAsset(asset?.metadata),
        ({ data: { amount } }) => amount,
        ({ data: { percentageLocked } }) => percentageLocked,
      ],
      ["desc", "desc", "desc"]
    );
  }, [mergedPosition?.value, sortConfig]);

  /**
   * Memoized columns for the table.
   */
  const columns: TPositionTableColumn = useMemo(() => {
    return [
      {
        tableCellProps: {
          render: ({ asset, pool }) => {
            if (pool) {
              return (
                <PoolComponent
                  pool={pool}
                  badges={{ ident: false, lpFee: false, version: true }}
                  inline
                />
              );
            }

            if (asset) {
              return (
                <AssetWithAssetName
                  assets={asset.metadata}
                  assetName={getAssetName(asset.metadata)}
                  assetNameSize="sm"
                  size="sm"
                />
              );
            }

            return null;
          },
        },
      },
      {
        tableCellProps: {
          render: ({ asset, assetAmount, pool, totalAmountInADA, totalAmountInUSD }) => {
            if (asset) {
              const isLP = !!pool || isLPAsset(asset.metadata.policyId);
              const assetDecimals = isLP
                ? // since we're rendering LPs converted to their ADA value, we need to use ADA's decimals
                  ADA_METADATA.decimals
                : asset.metadata.decimals;
              const maximumFractionDigits = Number.isInteger(
                totalAmountInADA?.value.toNumber() ?? 0
              )
                ? // If the amount is a whole number (i.e. 5), don't show decimals (would render i.e. ₳5.00 otherwise)
                  0
                : assetDecimals;

              return (
                <div className="inline-flex w-fit items-center gap-1">
                  {isLP && (
                    <Text tag="span" size="sm">
                      ~{" "}
                    </Text>
                  )}
                  <Balance
                    formatOpts={{ maximumFractionDigits }}
                    locale={currentLocale}
                    privacy={privacyEnabled}
                    size="sm"
                    showSymbol={SundaeUtils.isAdaAsset(asset.metadata) || isLP}
                    usdValue={totalAmountInUSD}
                    value={isLP ? totalAmountInADA : assetAmount}
                  />
                </div>
              );
            }
          },
        },
      },
      {
        tableCellProps: {
          render: ({ percentageLocked }) => {
            if (!percentageLocked) return "-";
            return (
              <Percentage
                locale={currentLocale}
                formatOpts={{
                  // @ts-ignore
                  roundingMode: "floor", // Experimental, but working for modern browsers: https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Intl/NumberFormat/NumberFormat#roundingmode
                }}
                privacy={privacyEnabled}
                size="sm"
                value={percentageLocked}
              />
            );
          },
        },
      },
    ];
  }, [currentLocale, privacyEnabled, t]);

  return {
    columnWidths,
    columns,
    handleSort,
    sortConfig,
    tableHeaders,
    tableData,
  };
};
